import React, { useState, useEffect } from 'react';
import { pacsAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import { printRadiologyReport } from '../../utils/printSlip';

const STATUS_COLOR = { Draft: '#eab308', Finalized: '#8b5cf6', Approved: '#22c55e', Rejected: '#ef4444' };
const STATUS_BG    = { Draft: '#fefce8', Finalized: '#f5f3ff', Approved: '#f0fdf4', Rejected: '#fef2f2' };

export default function RadiologyApproval() {
    const [queue, setQueue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [filter, setFilter] = useState('Finalized');

    const load = async () => {
        setLoading(true);
        try {
            const res = await pacsAPI.getApprovalQueue();
            setQueue(res.data);
        } catch { toast.error('Failed to load approval queue'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const approve = async () => {
        if (!window.confirm('Confirm sign-off on this radiology report? This action cannot be undone.')) return;
        setSaving(true);
        try {
            await pacsAPI.approveReport({ order_id: selected.id, approval_notes: notes });
            toast.success('Report approved and signed off');
            setSelected(null);
            setNotes('');
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Approval failed');
        } finally { setSaving(false); }
    };

    const reject = async () => {
        if (!notes.trim()) return toast.error('Please enter rejection reason');
        setSaving(true);
        try {
            await pacsAPI.rejectReport({ order_id: selected.id, rejection_notes: notes });
            toast.success('Report sent back for revision');
            setSelected(null);
            setNotes('');
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Rejection failed');
        } finally { setSaving(false); }
    };

    const filtered = filter === 'ALL' ? queue : queue.filter(o => o.report_status === filter);

    if (selected) {
        return (
            <div style={{ maxWidth: 900 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                    <button onClick={() => { setSelected(null); setNotes(''); }}
                        style={{ padding: '6px 14px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>
                        ← Back
                    </button>
                    <span style={{ fontSize: 24 }}>📋</span>
                    <div>
                        <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0369a1', margin: 0 }}>Review Report — {selected.order_number}</h2>
                        <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>{selected.modality} | {selected.full_name} ({selected.mrn})</p>
                    </div>
                </div>

                {/* Patient info */}
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 18, marginBottom: 16 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                        {[
                            { label: 'Patient', value: `${selected.full_name} (${selected.mrn})` },
                            { label: 'Age / Gender', value: `${selected.age_years || '?'}y / ${selected.gender || '?'}` },
                            { label: 'Modality', value: selected.modality },
                            { label: 'Study', value: selected.test_name || selected.body_part || '—' },
                            { label: 'Priority', value: selected.priority },
                            { label: 'Radiologist', value: selected.radiologist_name || '—' },
                            { label: 'Finalized At', value: selected.report_finalized_at ? new Date(selected.report_finalized_at).toLocaleString('en-PK') : '—' },
                            { label: 'Status', value: selected.report_status },
                        ].map(item => (
                            <div key={item.label}>
                                <div style={{ fontSize: 10, fontWeight: 700, color: '#0369a1', textTransform: 'uppercase' }}>{item.label}</div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginTop: 2 }}>{item.value}</div>
                            </div>
                        ))}
                    </div>
                    {selected.clinical_indication && (
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #bfdbfe' }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#0369a1' }}>CLINICAL INDICATION: </span>
                            <span style={{ fontSize: 13 }}>{selected.clinical_indication}</span>
                        </div>
                    )}
                </div>

                {/* Report content */}
                <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: 20, marginBottom: 16 }}>
                    <h3 style={{ fontSize: 13, fontWeight: 700, color: '#0369a1', marginBottom: 16 }}>Radiology Report</h3>
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 8, textTransform: 'uppercase' }}>Findings</div>
                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 7, padding: '12px 14px', fontSize: 13, lineHeight: 1.6, minHeight: 100, whiteSpace: 'pre-wrap' }}>
                            {selected.findings || <span style={{ color: '#94a3b8' }}>No findings entered</span>}
                        </div>
                    </div>
                    <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 8, textTransform: 'uppercase' }}>Impression / Conclusion</div>
                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 7, padding: '12px 14px', fontSize: 13, lineHeight: 1.6, minHeight: 60, whiteSpace: 'pre-wrap' }}>
                            {selected.impression || <span style={{ color: '#94a3b8' }}>No impression entered</span>}
                        </div>
                    </div>
                </div>

                {/* Approval action */}
                {selected.report_status === 'Finalized' && (
                    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 20 }}>
                        <h3 style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', marginBottom: 14 }}>Sign-off Decision</h3>
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 6 }}>Notes (required for rejection, optional for approval)</label>
                            <textarea
                                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box', height: 80, resize: 'vertical' }}
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                                placeholder="Enter approval notes or rejection reason..."
                            />
                        </div>
                        <div style={{ display: 'flex', gap: 12 }}>
                            <button onClick={approve} disabled={saving}
                                style={{ padding: '11px 28px', background: saving ? '#9ca3af' : '#16a34a', color: 'white', border: 'none', borderRadius: 7, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                                ✅ Approve & Sign Off
                            </button>
                            <button onClick={reject} disabled={saving}
                                style={{ padding: '11px 28px', background: saving ? '#9ca3af' : '#dc2626', color: 'white', border: 'none', borderRadius: 7, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                                ❌ Reject — Send Back
                            </button>
                        </div>
                    </div>
                )}

                {selected.report_status === 'Approved' && (
                    <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '12px 16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <span style={{ fontWeight: 700, color: '#16a34a' }}>✅ Report Approved & Signed Off</span>
                                {selected.approval_notes && <div style={{ fontSize: 12, color: '#15803d', marginTop: 4 }}>{selected.approval_notes}</div>}
                            </div>
                            <div style={{ display: 'flex', gap: 10 }}>
                                <button onClick={() => printRadiologyReport(selected)}
                                    style={{ padding: '10px 22px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                                    🖨️ Print Report
                                </button>
                                <button onClick={() => {
                                    const token = localStorage.getItem('hims_token');
                                    const url = `/api/pacs/orders/${selected.id}/pdf`;
                                    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                                        .then(res => res.blob())
                                        .then(blob => {
                                            const link = document.createElement('a');
                                            link.href = URL.createObjectURL(blob);
                                            link.download = `Radiology-Report-${selected.mrn}-${selected.id}.pdf`;
                                            link.click();
                                        })
                                        .catch(() => toast.error('PDF download failed'));
                                }}
                                    style={{ padding: '10px 22px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                                    📄 Download PDF
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {selected.report_status === 'Rejected' && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '12px 16px' }}>
                        <span style={{ fontWeight: 700, color: '#dc2626' }}>❌ Report Rejected</span>
                        {selected.approval_notes && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 4 }}>Reason: {selected.approval_notes}</div>}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div style={{ maxWidth: 1000 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <span style={{ fontSize: 32 }}>📋</span>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0369a1', margin: 0 }}>Report Approval & Sign-off</h2>
                    <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Senior Radiologist — review and approve finalized reports</p>
                </div>
                <button onClick={load} style={{ marginLeft: 'auto', padding: '8px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>
                    🔄 Refresh
                </button>
            </div>

            {/* Summary counts */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                {['ALL', 'Finalized', 'Approved', 'Rejected', 'Draft'].map(s => {
                    const count = s === 'ALL' ? queue.length : queue.filter(o => o.report_status === s).length;
                    return (
                        <button key={s} onClick={() => setFilter(s)}
                            style={{ padding: '6px 16px', borderRadius: 16, border: `2px solid ${filter === s ? (STATUS_COLOR[s] || '#1e3a5f') : '#e2e8f0'}`, cursor: 'pointer', fontSize: 12, fontWeight: filter === s ? 700 : 400,
                                background: filter === s ? (STATUS_BG[s] || '#eff6ff') : 'white', color: filter === s ? (STATUS_COLOR[s] || '#0369a1') : '#374151' }}>
                            {s} ({count})
                        </button>
                    );
                })}
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading...</div>
            ) : filtered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, background: 'white', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                    <p style={{ color: '#64748b' }}>No reports in this category</p>
                </div>
            ) : (
                <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc' }}>
                                {['Order No.', 'Patient', 'Modality / Study', 'Priority', 'Radiologist', 'Finalized At', 'Status', ''].map(h => (
                                    <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(o => (
                                <tr key={o.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '11px 14px', fontSize: 12, fontWeight: 700, color: '#0369a1' }}>{o.order_number}</td>
                                    <td style={{ padding: '11px 14px' }}>
                                        <div style={{ fontSize: 13, fontWeight: 600 }}>{o.full_name}</div>
                                        <div style={{ fontSize: 11, color: '#64748b' }}>{o.mrn} | {o.age_years ? `${o.age_years}y` : '?'} / {o.gender || '?'}</div>
                                    </td>
                                    <td style={{ padding: '11px 14px' }}>
                                        <div style={{ fontSize: 12, fontWeight: 600 }}>{o.modality}</div>
                                        <div style={{ fontSize: 11, color: '#64748b' }}>{o.test_name || o.body_part || '—'}</div>
                                    </td>
                                    <td style={{ padding: '11px 14px' }}>
                                        {o.priority === 'STAT'
                                            ? <span style={{ background: '#fee2e2', color: '#dc2626', padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 800 }}>🚨 STAT</span>
                                            : <span style={{ fontSize: 12, color: '#64748b' }}>{o.priority || 'Routine'}</span>}
                                    </td>
                                    <td style={{ padding: '11px 14px', fontSize: 12 }}>{o.radiologist_name || '—'}</td>
                                    <td style={{ padding: '11px 14px', fontSize: 12 }}>{o.report_finalized_at ? new Date(o.report_finalized_at).toLocaleString('en-PK', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                                    <td style={{ padding: '11px 14px' }}>
                                        <span style={{ background: STATUS_BG[o.report_status] || '#f1f5f9', color: STATUS_COLOR[o.report_status] || '#475569', padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
                                            {o.report_status}
                                        </span>
                                    </td>
                                    <td style={{ padding: '11px 14px' }}>
                                        <button onClick={() => { setSelected(o); setNotes(''); }}
                                            style={{ padding: '5px 14px', background: o.report_status === 'Finalized' ? '#0369a1' : '#64748b', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                            {o.report_status === 'Finalized' ? '📋 Review' : '👁 View'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
